import React, {FC, useEffect, useMemo, useRef, useState} from 'react';

//MUI
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

//GEOCOMPONENTS
import Map from '@geomatico/geocomponents/Map/Map';

//GEPROCAR
import Sponsors from '../../components/Sponsors';
import sourcesConstructor from '../../components/utils/sources';
import ViewSelector from '@/components/ViewSelector/ViewSelector';

//UTILS
import {MAP_DETAILS, MAP_DETAIL_STYLES, PRIVATE_LAYERS} from '@/config';
import {AnyLayer, MapLayerMouseEvent, Style} from 'mapbox-gl';
import {SxProps} from '@mui/system/styleFunctionSx/styleFunctionSx';
import {Profile} from '@/domain/entities/Profile';
import {bboxType, FilterGroup, UuidOptionColor, ViewBy, ViewportType} from '@/domain/services/Clas';
import type {MapRef} from 'react-map-gl';
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material';
import Paper from '@mui/material/Paper';
import layerConstructor from '@/components/utils/layers';

const legendsGroup: SxProps = {
  position: 'absolute',
  left: 0,
  bottom: 70,
  m: 1,
  p: 0,
  width: 252
};

export type MainContentProps = {
  selectedPrecinctId?: number,
  crops?: Array<UuidOptionColor>,
  uses?: Array<UuidOptionColor>,
  filters: FilterGroup,
  filteredPrecinctBbox: bboxType | null,//Modificación de bbox al filtrar
  mapStyle: Style | string,
  mapDetail: Array<string>,
  overlayIds: Array<string>,
  profile?: Profile,
  year?: number,
  viewport: ViewportType,
  onPointSelected: (pointOfInterest: {lng: number, lat: number} | undefined) => void,
  onViewportChange: (viewport: ViewportType) => void
};

const MainContent: FC<MainContentProps> = ({
  selectedPrecinctId,
  crops,
  uses,
  filters,
  filteredPrecinctBbox,//Modificación de bbox al filtrar
  mapStyle,
  mapDetail,
  overlayIds,
  profile,
  year,
  viewport,
  onPointSelected,
  onViewportChange
}) => {

  const [viewBy, setViewBy] = useState<ViewBy>(ViewBy.USER);
  const mapRef = useRef<MapRef>(null);

  //Modificación de bbox al filtrar
  useEffect(() => {
    if(filteredPrecinctBbox) {
      const {xmin, ymin, xmax, ymax} = filteredPrecinctBbox;
      if (xmin && xmax && ymin && ymax) {
        mapRef.current?.fitBounds([[xmin, ymin], [xmax, ymax]],
          {
            duration: 2000,
            padding: {
              left: 50,
              right: 50,
              top: 50,
              bottom: 250
            }
          }
        );
      }
    }
  }, [filteredPrecinctBbox]);

  const detailLayers: Array<AnyLayer> = useMemo(() => {
    const layersResult = [];
    if (mapDetail.includes(MAP_DETAILS.REDNATURA)) {
      layersResult.push(...layerConstructor.redNatura());
    }
    if (mapDetail.includes(MAP_DETAILS.CATASTRO)) {
      layersResult.push(...layerConstructor.catastro());
    }
    if (mapDetail.includes(MAP_DETAILS.PROTECTION)) {
      layersResult.push(...layerConstructor.protection());
    }
    return layersResult;
  }, [mapDetail]);

  const overlays: Array<AnyLayer> = useMemo(() => {
    const layersResult = [];
    if(overlayIds.length) {
      layersResult.push(...layerConstructor.overlay(overlayIds));
    }
    return layersResult;
  }, [overlayIds]);

  const sources = useMemo(() => sourcesConstructor(overlayIds, viewBy, filters, year, profile)
    , [profile, overlayIds, viewBy, filters, year]);

  const layers = useMemo(() => {
    if (profile && crops && uses) {
      return [
        ...overlays,
        ...detailLayers,
        ...layerConstructor.viewBy(),
      ];
    }
  }, [detailLayers, profile, selectedPrecinctId, filters, viewBy, overlays, crops, uses]);

  const handleMapClick = (e: MapLayerMouseEvent) => {
    onPointSelected({lng: e.lngLat.lng, lat: e.lngLat.lat});
  };

  const handleViewByChange = (newViewBy: string) => {
    if (Object.values(ViewBy).includes(newViewBy as ViewBy)) {
      setViewBy(newViewBy as ViewBy);
    }
  };
  const legendsToShow = [...mapDetail, ...overlayIds];
  const stylesToShow = [...MAP_DETAIL_STYLES, ...PRIVATE_LAYERS];
  return <Map
    ref={mapRef}
    mapStyle={mapStyle}
    viewport={viewport}
    sources={sources}
    layers={layers}
    onViewportChange={onViewportChange}
    onClick={handleMapClick}
    /*auth={[
      {
        urlMatch: process.env.GEOSERVER_URL || process.env.GEOSERVER_CETEMAS || '',
        user: process.env.GEOSERVER_USER || '',
        password: process.env.GEOSERVER_PASSWORD || '',
      }
    ]}*/
  >
    {
      (mapDetail.length > 0 || overlayIds.length > 0) &&
      <Paper sx={legendsGroup}>
        {
          legendsToShow.map(itemId => {
            const legendLayer = stylesToShow.filter(item => item.id === itemId)[0];
            return legendLayer && legendLayer.legendUrl && <Accordion key={`legend_${itemId}`}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant='overline' sx={{lineHeight: 1.5}}>{legendLayer.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box component="img" src={legendLayer.legendUrl} width={legendLayer.width}/>
              </AccordionDetails>
            </Accordion>;
          })
        }
      </Paper>
    }
    <Sponsors/>
    {
      crops && uses &&
      <ViewSelector
        crops={crops}
        uses={uses}
        viewBy={viewBy}
        onViewByChange={handleViewByChange}/>
    }
  </Map>;
};

export default MainContent;
